import type * as THREE from 'three';
import type { CanonicalEntity, Vec3 } from '@/shared/api/types';

export type { Vec3 };

export type MaterialProfile =
  | 'wood'
  | 'stone'
  | 'metal'
  | 'paint'
  | 'glass'
  | 'mirror'
  | 'appliance'
  | 'fabric';

/** Every profile the renderer, the wire and the backend buildspec validator
 * accept.  One owner: this list used to be written out by hand in the GLTF
 * loader, in BulkPanelDialog's own copy of the type, in the mutation body type
 * and in two picker literals. */
export const MATERIAL_PROFILES: readonly MaterialProfile[] = [
  'wood',
  'stone',
  'metal',
  'paint',
  'glass',
  'mirror',
  'appliance',
  'fabric',
];

/** The profiles the panel editors OFFER: MATERIAL_PROFILES without 'mirror'.
 *
 * A separate name, deliberately, instead of a silent second copy.  The two
 * lists disagree on exactly one value, and that disagreement is a product
 * question ("is mirror an editable panel profile?"), not a typo.  What is NOT
 * a product question, and is fixed: a panel already stored as a mirror is
 * shown as a mirror and is never rewritten to wood behind the user's back. */
export const EDITOR_MATERIAL_PROFILES: readonly MaterialProfile[] = [
  'wood',
  'stone',
  'metal',
  'paint',
  'glass',
  'appliance',
  'fabric',
];

export type GrainDirection = 'length' | 'width' | 'cross' | 'none';

export type LightPreset = 'studio' | 'showroom' | 'daylight' | 'photo';
export type HdriPreset = 'neutral' | 'warm' | 'cool';

export type AnimatedPart = {
  id: string;
  kind: 'door' | 'drawer';
  holder: THREE.Group;
  entity: CanonicalEntity;
  axis: 'x' | 'y';
  direction: 1 | -1;
  size: THREE.Vector3;
  basePosition: THREE.Vector3;
  baseRotation: THREE.Euler;
  baseQuaternion: THREE.Quaternion;
  targetOffset: number;
  targetRotation: number;
};

export type EntityRender = {
  entity: CanonicalEntity;
  mesh: THREE.Mesh | null;
  group: THREE.Group;
};

export type SavedView = {
  id: string;
  label: string;
  position: [number, number, number];
  target: [number, number, number];
  up: [number, number, number];
  zoom: number;
  projection: string;
  /**
   * Половина высоты ортографического кадра в миллиметрах — МАСШТАБ чертежа.
   *
   * У перспективы масштаб задаёт расстояние, и его уже несёт `position`. У
   * ортографии расстояние на масштаб не влияет вовсе: кадр живёт в
   * `top/bottom`. Без этого числа восстановленный план встал бы правильно, но
   * в чужом масштабе — и замер по нему был бы неверен.
   *
   * Необязательное: у ракурсов, снятых до появления второй камеры, его нет.
   * Отсутствие не подменяется числом — масштаб тогда пересчитывается подгонкой.
   */
  orthoHalfHeightMm?: number;
  createdAt: string;
};

/**
 * Один владелец позы камеры на весь вьюер.
 *
 * Раньше эта же форма второй раз объявлялась в `lib/pt-presets.ts`, и обе копии
 * совпадали только по случайности переписывания от руки: `SceneCanvas` передаёт
 * `CaptureRequest.cameraPreset` (отсюда) прямо в `computeViewPose` (оттуда).
 * Тройка координат берётся у `@/shared/api/types`, чтобы у неё тоже был один
 * владелец, а не четвёртая копия `[number, number, number]`.
 */
export type CameraPose = {
  projection?: 'perspective'|'orthographic';
  ortho?: {left:number;right:number;top:number;bottom:number;near:number;far:number;zoom:number};
  position: Vec3;
  target: Vec3;
  up: Vec3;
  fovDeg: number;
};

/**
 * A deterministic product-camera preset resolved against the current furniture
 * bounds by SceneCanvas.  It deliberately does not preserve the live orbit
 * camera: automatic evidence and catalog captures must be repeatable and must
 * frame the furniture rather than whatever the operator last inspected.
 */
export type ProductCameraPreset = 'hero' | 'front' | 'technical_oblique' | 'three_quarter_left' | 'side' | 'top';
export type FacadeFacing = '+x' | '-x' | '+z' | '-z';

export type CaptureRequest = {
  surfaceMode?: import('./lib/RenderScenePolicy').RenderSurfaceMode;
  id: number;
  label: string;
  projection: string;
  lightPreset: LightPreset;
  hdriPreset: HdriPreset;
  hdriIntensity?: number;
  sampleLevel: number;
  quality: 'standard' | 'final_photo' | 'client_photo';
  resolutionScale?: number;
  /** Viewer exposure carried into the PT render. */
  exposure?: number;
  /** Safe browser-only post controls; provenance is kept with the archived job. */
  desaturation?: number;
  depthOfField?: number;
  /** Per-job camera pose for multi-view batch renders. */
  cameraPose?: CameraPose;
  /** Resolve a product-camera pose from the current furniture bounds at capture time. */
  cameraPreset?: ProductCameraPreset;
  /** Dominant observed facade normal.  Avoids treating a side/rear as "front". */
  cameraFacing?: FacadeFacing;
};

export type CapturedRender = {
  id: number;
  label: string;
  dataUrl: string;
  samples: number;
  elapsedMs: number;
  cameraPose?: CameraPose;
  captureEngine?: 'viewport' | 'path-trace';
  /** Actual pixel size of `dataUrl`. The archive contract binds a render to its
   * revision by validating the real PNG IHDR, so a guessed size is a refusal:
   * the client used to send a constant 1600x900 while captures came out
   * 2560x1561 / 1280x780, and every archive attempt was rejected. */
  width?: number;
  height?: number;
};

export type RenderInspectorState = {
  state: 'idle' | 'preparing' | 'rendering' | 'done' | 'error';
  label?: string;
  engine?: 'viewport' | 'path-trace';
  samples?: number;
  targetSamples?: number;
  noise?: number | null;
  elapsedMs?: number;
  resolution?: [number, number];
  hdriPreset?: HdriPreset;
  hdriRotation?: number;
  lightTemperature?: number;
  exposure?: number;
  message?: string;
};
